import { FC, Fragment, useMemo } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { ITeam } from "../../../../models/Team";
import { IDetailsTeamResponse } from "../../../../models/endpoints/Team";
import { Grid, GridItem, Skeleton } from "@chakra-ui/react";
import { MiddleCard } from "../MiddleCard/MiddleCard";
import dayjs from "dayjs";
import {
  calculateMean,
  linearInterpolation,
} from "../../../../utils/functions";

export const SecondRow: FC<{
  query: UseQueryResult<IDetailsTeamResponse, Error>;
  teamQuery: UseQueryResult<ITeam, Error>;
  creatingPdf: boolean;
}> = ({ query, creatingPdf }) => {
  const date = dayjs().subtract(7, "days");
  const {
    objectReadiness,
    percObjectReadiness,
    subjectReadiness,
    percSubjectReadiness,
    currentPartecipation,
    totalCurrEvents,
    totalPercentageEvents,
    precentagePartecipation,
  } = useMemo(() => {
    if (!query.data) return {};
    /**
     * Settimana corrente contro settimana precedente.
     *
     * Il ramo `prev` esisteva ma non veniva mai riempito (entrambi i casi
     * facevano `push` su `curr`): il confronto era quindi sempre "tutto contro
     * niente" e la variazione mostrata coincideva col valore stesso — ecco il
     * "+1%" sotto una readiness dell'1%.
     *
     * Si contano inoltre solo i giorni con una compilazione reale
     * (`availableFactors` non vuoto): i giorni vuoti valgono 0 per costruzione
     * e trascinerebbero la media a fondo.
     */
    const conDato = (r: IDetailsTeamResponse["totalRecoveryRecords"][number]) =>
      !r.availableFactors || r.availableFactors.length > 0;

    const { prev, curr } = query.data.totalRecoveryRecords.filter(conDato).reduce(
      (acc, record) => {
        if (dayjs(record.date).isBefore(date)) {
          acc.prev.push(record);
        } else {
          acc.curr.push(record);
        }
        return acc;
      },
      {
        prev: [] as IDetailsTeamResponse["totalRecoveryRecords"],
        curr: [] as IDetailsTeamResponse["totalRecoveryRecords"],
      }
    );

    /**
     * Le due componenti valgono 0 quando la loro fonte manca (nessun Hooper,
     * nessun CMJ/FCR/HRV). Mediarle con gli zeri farebbe scendere la readiness
     * di chi ha compilato solo una delle due: si tiene solo ciò che esiste, e
     * si restituisce `null` quando non esiste niente.
     */
    const media = (valori: number[]): number | null =>
      valori.length ? calculateMean(valori) : null;

    const soggettive = (
      records: IDetailsTeamResponse["totalRecoveryRecords"]
    ) => records.flatMap((i) => (i.subjectInterpolation > 0 ? i.subjectInterpolation : []));
    const oggettive = (records: IDetailsTeamResponse["totalRecoveryRecords"]) =>
      records.flatMap((i) => (i.objectInterpolation > 0 ? i.objectInterpolation : []));

    const prevSubjectReadiness = media(soggettive(prev));
    const currSubjectReadiness = media(soggettive(curr));
    const prevObjectReadiness = media(oggettive(prev));
    const currObjectReadiness = media(oggettive(curr));

    /** Variazione mostrabile solo se esistono ENTRAMBI i periodi da confrontare. */
    const delta = (adesso: number | null, prima: number | null) =>
      adesso === null || prima === null
        ? null
        : Number((adesso - prima).toFixed(0));

    const {
      totalPrevUsersEvents,
      partecipationPrevEvents,
      totalCurrUsersEvents,
      partecipationCurrEvents,
      totalCurrEvents,
      totalPrevEvents,
    } = query.data.events.reduce(
      (acc, curr) => {
        if (dayjs(curr.startDate).isBefore(date)) {
          acc.totalPrevEvents = acc.totalPrevEvents + 1;

          acc.totalPrevUsersEvents =
            curr.userIds.length + acc.totalPrevUsersEvents;
          acc.partecipationPrevEvents =
            curr.eventResults.filter((i) => !i.skipped).length +
            acc.partecipationPrevEvents;
        } else {
          acc.totalCurrEvents = acc.totalCurrEvents + 1;

          acc.totalCurrUsersEvents =
            curr.userIds.length + acc.totalCurrUsersEvents;
          acc.partecipationCurrEvents =
            curr.eventResults.filter((i) => !i.skipped).length +
            acc.partecipationCurrEvents;
        }
        return acc;
      },
      {
        totalPrevEvents: 0 as number,
        totalCurrEvents: 0 as number,
        totalPrevUsersEvents: 0 as number,
        partecipationPrevEvents: 0 as number,
        totalCurrUsersEvents: 0 as number,
        partecipationCurrEvents: 0 as number,
      }
    );
    /**
     * Partecipazione = compilazioni raccolte su compilazioni attese.
     *
     * Senza eventi nel periodo il denominatore è 0: `linearInterpolation`
     * restituiva comunque un numero, e il confronto con una settimana vuota
     * produceva il "-100%" su una squadra che non aveva ancora giocato. Senza
     * attese non c'è una percentuale da mostrare: è `null`.
     */
    const percentuale = (fatte: number, attese: number): number | null =>
      attese > 0 ? linearInterpolation(fatte, 0, attese, 0, 100) : null;

    const currentPartecipation = percentuale(
      partecipationCurrEvents,
      totalCurrUsersEvents
    );
    const prevPartecipation = percentuale(
      partecipationPrevEvents,
      totalPrevUsersEvents
    );

    return {
      totalCurrEvents,
      totalPrevEvents,
      currentPartecipation,
      prevPartecipation,
      // La variazione del numero di attività ha senso solo se c'è una settimana
      // precedente con cui confrontarsi.
      totalPercentageEvents: totalPrevEvents > 0 ? totalCurrEvents - totalPrevEvents : null,
      precentagePartecipation: delta(currentPartecipation, prevPartecipation),
      objectReadiness:
        currObjectReadiness === null
          ? null
          : Number(currObjectReadiness.toFixed(0)),
      percObjectReadiness: delta(currObjectReadiness, prevObjectReadiness),
      subjectReadiness:
        currSubjectReadiness === null
          ? null
          : Number(currSubjectReadiness.toFixed(0)),
      percSubjectReadiness: delta(currSubjectReadiness, prevSubjectReadiness),
    };
  }, [query.data]);

  /**
   * Copertura delle due readiness.
   *
   * Attenzione al periodo: queste card mostrano la SETTIMANA corrente (è il
   * termine di paragone del badge di variazione), mentre le tre card in alto
   * mediano sugli ultimi 14 giorni. Scrivere "media team" su entrambe le righe
   * faceva sembrare che parlassero dello stesso intervallo — e i numeri, non
   * coincidendo, sembravano sbagliati.
   */
  const copertura = (valore: number | null | undefined) => {
    const conDato = query.data?.coverage?.recovery;
    const totale = query.data?.coverage?.athletes;
    if (valore === null || valore === undefined) return "nessun dato";
    if (typeof conDato !== "number" || typeof totale !== "number")
      return "ultimi 7 giorni";
    return conDato === totale
      ? `ultimi 7 giorni · ${totale} atleti`
      : `ultimi 7 giorni · fino a ${conDato} di ${totale} atleti`;
  };

  return (
    <Grid
      templateRows="repeat(1, 1fr)"
      templateColumns="repeat(5, 1fr)"
      gap={4}
    >
      <GridItem colSpan={1}>
        <MiddleCard
          creatingPdf={creatingPdf}
          loading={query.isPending}
          title="Readiness oggettiva"
          label={objectReadiness}
          sublabel={copertura(objectReadiness)}
          percentage={percObjectReadiness ?? null}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <MiddleCard
          creatingPdf={creatingPdf}
          loading={query.isPending}
          title="Readiness soggettiva"
          label={subjectReadiness}
          sublabel={copertura(subjectReadiness)}
          percentage={percSubjectReadiness ?? null}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <MiddleCard
          creatingPdf={creatingPdf}
          loading={query.isPending}
          title="Attività giornaliere"
          label={totalCurrEvents}
          showPercentage={false}
          sublabel="ultimi 7 giorni"
          showPercentageLabel={false}
          percentage={totalPercentageEvents ?? null}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <MiddleCard
          creatingPdf={creatingPdf}
          loading={query.isPending}
          title="Partecipazione"
          label={currentPartecipation}
          sublabel={
            currentPartecipation === null
              ? "nessuna attività"
              : "ultimi 7 giorni"
          }
          percentage={precentagePartecipation ?? null}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <MiddleCard
          creatingPdf={creatingPdf}
          loading={query.isPending}
          title="Membri del team"
          label={query.data?.readinessByUser.length}
          sublabel=""
          showPercentageLabel={false}
          BottomRightComponent={<Fragment />}
          percentage={0}
        />
      </GridItem>
      {/* <GridItem colSpan={1}>
        <MiddleCard
          loading={query.isPending}
          title="Infortunati"
          label={6}
          showPercentageLabel={false}
          sublabel=""
          BottomRightComponent={<Fragment />}
          percentage={-1.1}
        />
      </GridItem> */}
    </Grid>
  );
};
